import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Plus, X, Image } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GalleryPage = () => {
  const { studio, setStudio } = useOutletContext();
  const { getAuthHeader } = useAuth();
  const [saving, setSaving] = useState(false);
  const [images, setImages] = useState(studio?.gallery_images || []);
  const [newImageUrl, setNewImageUrl] = useState('');

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/studios/me`, { gallery_images: images }, { headers: getAuthHeader() });
      setStudio(response.data);
      toast.success('Gallery updated');
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleAddImage = () => {
    if (newImageUrl.trim()) {
      setImages([...images, newImageUrl.trim()]);
      setNewImageUrl('');
    }
  };

  const handleRemoveImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="gallery-page"
    >
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">Gallery</h1>
          <p className="text-white/60">Showcase your best work</p>
        </div>
        <Button 
          onClick={handleSave}
          disabled={saving}
          className="bg-[#FFFFFF] text-black hover:bg-[#E0E0E0]"
          data-testid="save-gallery-btn"
        >
          {saving ? <span className="spinner w-5 h-5"></span> : 'Save Changes'}
        </Button>
      </div>

      {/* Add Image */}
      <div className="bg-[#0F0F0F] border border-white/10 rounded-sm p-6 mb-8">
        <Label className="text-white/70 mb-2 block">Add Image URL</Label>
        <div className="flex gap-2">
          <Input
            type="url"
            value={newImageUrl}
            onChange={(e) => setNewImageUrl(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddImage()}
            placeholder="https://... (use Unsplash, Cloudinary, etc.)"
            className="bg-[#171717] border-white/10 flex-1"
            data-testid="gallery-url-input"
          />
          <Button
            onClick={handleAddImage}
            className="bg-[#FFFFFF] text-black hover:bg-[#E0E0E0]"
          >
            <Plus size={18} className="mr-2" /> Add
          </Button>
        </div>
        <p className="text-white/40 text-xs mt-2">
          Tip: Use image hosting services like Unsplash, Imgur, or Cloudinary for your photos
        </p>
      </div>

      {/* Gallery Grid */}
      {images.length === 0 ? (
        <div className="text-center py-16 bg-[#0F0F0F] border border-white/10 rounded-sm">
          <Image className="w-12 h-12 mx-auto mb-4 text-white/20" />
          <p className="text-white/40 mb-4">No images in your gallery yet</p>
          <p className="text-white/30 text-sm">Add image URLs above to showcase your work</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((img, index) => (
            <div 
              key={index}
              className="group relative aspect-video bg-[#0F0F0F] rounded-sm overflow-hidden"
            >
              <img 
                src={img} 
                alt={`Gallery ${index + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%230F0F0F" width="100" height="100"/><text x="50" y="50" text-anchor="middle" fill="%23333" font-size="12">Error</text></svg>';
                }}
              />
              <button
                onClick={() => handleRemoveImage(index)}
                className="absolute top-2 right-2 w-8 h-8 bg-black/60 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default GalleryPage;
